import React, { useState, useEffect } from 'react';
import { Gift, Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { useRewards } from '../../hooks/useRewards';
import { useAuth } from '../../contexts/AuthContext';
import { deleteImage } from '../../services/imageService';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageUploader from '../common/ImageUploader';

const RewardsManager = () => {
  const { user } = useAuth();
  const { 
    rewards, 
    loading, 
    error, 
    fetchRewards, 
    createNewReward, 
    updateExistingReward, 
    removeReward 
  } = useRewards(user?.companyId);

  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockUpdateReward, setStockUpdateReward] = useState(null);
  const [newStockQuantity, setNewStockQuantity] = useState('');
  const [batchCode, setBatchCode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pointsRequired: '',
    type: 'product',
    value: '',
    imageUrl: '',
    imagePath: '',
    // Campos de estoque - para produtos e aluguéis
    stockQuantity: '',
    hasStockControl: true,
    lowStockThreshold: '5',
    batchCode: '',
    // Campos específicos para aluguel
    rentalDuration: '7'
  });

  useEffect(() => {
    if (user?.companyId) {
      fetchRewards(!showInactive);
    }
  }, [user?.companyId, showInactive, fetchRewards]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Converter campos numéricos de string para número
      const dataToSave = {
        ...formData,
        pointsRequired: parseInt(formData.pointsRequired) || 0,
        stockQuantity: formData.hasStockControl ? (parseInt(formData.stockQuantity) || 0) : 0,
        lowStockThreshold: formData.hasStockControl ? (parseInt(formData.lowStockThreshold) || 5) : 5,
        rentalDuration: formData.type === 'rental' ? (parseInt(formData.rentalDuration) || 7) : null
      };

      if (editingReward) {
        await updateExistingReward(editingReward.id, dataToSave);
      } else {
        await createNewReward(dataToSave);
      }
      
      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error('Erro ao salvar prêmio:', err);
    }
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description,
      pointsRequired: reward.pointsRequired.toString(),
      type: reward.type,
      value: reward.value || '',
      imageUrl: reward.imageUrl || '',
      imagePath: reward.imagePath || '',
      // Campos de estoque - para produtos e aluguéis
      stockQuantity: (reward.type === 'product' || reward.type === 'rental') ? (reward.stockQuantity || 0).toString() : '',
      hasStockControl: (reward.type === 'product' || reward.type === 'rental') ? (reward.hasStockControl !== false) : false,
      lowStockThreshold: (reward.type === 'product' || reward.type === 'rental') ? (reward.lowStockThreshold || 5).toString() : '5',
      batchCode: reward.batchCode || '',
      // Campos específicos para aluguel
      rentalDuration: reward.type === 'rental' ? (reward.rentalDuration || 7).toString() : '7'
    });
    setShowModal(true);
  };

  const handleDelete = async (rewardId) => {
    if (window.confirm('Tem certeza que deseja desativar este prêmio?')) {
      try {
        await removeReward(rewardId);
      } catch (err) {
        console.error('Erro ao deletar prêmio:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      pointsRequired: '',
      type: 'product',
      value: '',
      imageUrl: '',
      imagePath: '',
      // Campos de estoque - para produtos e aluguéis
      stockQuantity: '',
      hasStockControl: true,
      lowStockThreshold: '5',
      batchCode: '',
      // Campos específicos para aluguel
      rentalDuration: '7'
    });
    setEditingReward(null);
  };

  const handleStockUpdate = (reward) => {
    setStockUpdateReward(reward);
    setNewStockQuantity(reward.stockQuantity.toString());
    setBatchCode('');
    setShowStockModal(true);
  };

  const submitStockUpdate = async (e) => {
    e.preventDefault();
    if (!stockUpdateReward) return;

    try {
      const { updateRewardStock } = await import('../../services/rewardsService');
      await updateRewardStock(
        stockUpdateReward.id, 
        parseInt(newStockQuantity), 
        batchCode,
        user?.uid,
        user?.name || user?.email
      );
      
      // Recarregar lista de prêmios
      await fetchRewards();
      
      // Fechar modal
      setShowStockModal(false);
      setStockUpdateReward(null);
      setNewStockQuantity('');
      setBatchCode('');
      
      alert('Estoque atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar estoque:', err);
      alert('Erro ao atualizar estoque: ' + err.message);
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      product: 'Produto',
      cashback: 'Cashback',
      discount: 'Desconto',
      service: 'Serviço',
      rental: 'Aluguel'
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      product: 'bg-blue-100 text-blue-800',
      cashback: 'bg-green-100 text-green-800',
      discount: 'bg-yellow-100 text-yellow-800',
      service: 'bg-purple-100 text-purple-800',
      rental: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading && rewards.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Gift className="mr-2" />
            Gerenciar Prêmios
          </h1>
          <p className="text-gray-600 mt-1">
            Configure os prêmios que seus clientes podem resgatar com pontos
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              showInactive 
                ? 'bg-gray-600 text-white' 
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            {showInactive ? <EyeOff size={16} /> : <Eye size={16} />}
            {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
          </button>
          
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Novo Prêmio
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        {rewards.length === 0 ? (
          <div className="p-8 text-center">
            <Gift className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum prêmio cadastrado
            </h3>
            <p className="text-gray-600 mb-4">
              Comece criando o primeiro prêmio para seus clientes
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Criar Primeiro Prêmio
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Imagem
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prêmio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pontos Necessários
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estoque
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lote
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewards.map((reward) => (
                  <tr key={reward.id} className={!reward.isActive ? 'opacity-50' : ''}>
                    <td className="px-6 py-4">
                      <div className="w-16 h-16">
                        {reward.imageUrl ? (
                          <img
                            src={reward.imageUrl}
                            alt={reward.name}
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                            <Gift size={20} className="text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {reward.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {reward.description}
                        </div>
                        {reward.value && (
                          <div className="text-sm text-gray-600 mt-1">
                            Valor: {reward.value}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(reward.type)}`}>
                        {getTypeLabel(reward.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {reward.pointsRequired} pontos
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {(reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl !== false ? (
                          <div>
                            <span className={`font-medium ${
                              reward.stockQuantity <= 0 ? 'text-red-600' :
                              reward.isLowStock ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {reward.stockQuantity} {reward.type === 'rental' ? 'disponíveis' : 'unidades'}
                            </span>
                            {reward.isLowStock && reward.stockQuantity > 0 && (
                              <div className="text-xs text-yellow-600">
                                {reward.type === 'rental' ? 'Poucos itens disponíveis' : 'Estoque baixo'}
                              </div>
                            )}
                            {reward.stockQuantity <= 0 && (
                              <div className="text-xs text-red-600">
                                {reward.type === 'rental' ? 'Nenhum item disponível' : 'Fora de estoque'}
                              </div>
                            )}
                          </div>
                        ) : (reward.type === 'product' || reward.type === 'rental') ? (
                          <span className="text-gray-500 text-sm">Ilimitado</span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reward.batchCode || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        reward.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {reward.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(reward)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar prêmio"
                        >
                          <Edit2 size={16} />
                        </button>
                        {(reward.type === 'product' || reward.type === 'rental') && reward.hasStockControl !== false && (
                          <button
                            onClick={() => handleStockUpdate(reward)}
                            className="text-green-600 hover:text-green-900"
                            title="Atualizar estoque"
                          >
                            📦
                          </button>
                        )}
                        {reward.isActive && (
                          <button
                            onClick={() => handleDelete(reward.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Desativar prêmio"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para criar/editar prêmio */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingReward ? 'Editar Prêmio' : 'Novo Prêmio'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Prêmio
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: 1kg de Açaí"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descrição detalhada do prêmio"
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo do Prêmio
              </label>
              <select
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFormData(prev => ({ 
                    ...prev, 
                    type: newType,
                    // Limpar campos de estoque se não for produto ou aluguel
                    stockQuantity: (newType === 'product' || newType === 'rental') ? prev.stockQuantity : '',
                    hasStockControl: (newType === 'product' || newType === 'rental') ? prev.hasStockControl : false,
                    lowStockThreshold: (newType === 'product' || newType === 'rental') ? prev.lowStockThreshold : '5',
                    // Limpar campos de aluguel se não for aluguel
                    rentalDuration: newType === 'rental' ? prev.rentalDuration : ''
                  }));
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="product">Produto</option>
                <option value="rental">Aluguel</option>
                <option value="cashback">Cashback</option>
                <option value="discount">Desconto</option>
                <option value="service">Serviço</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pontos Necessários
              </label>
              <input
                type="number"
                value={formData.pointsRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, pointsRequired: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="300"
                min="1"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor/Detalhes {formData.type === 'cashback' && '(R$)'}
            </label>
            <input
              type="text"
              value={formData.value}
              onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={
                formData.type === 'cashback' 
                  ? '50.00' 
                  : formData.type === 'discount'
                  ? '10% de desconto'
                  : 'Informações adicionais'
              }
            />
          </div>

          {/* Controle de Estoque - Para Produtos e Aluguéis */}
          {(formData.type === 'product' || formData.type === 'rental') && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-medium text-gray-700">
                  Controle de Estoque
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.hasStockControl}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasStockControl: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    Ativar controle de estoque
                  </span>
                </div>
              </div>

              {formData.hasStockControl && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantidade {formData.type === 'rental' ? 'Disponível para Aluguel' : 'em Estoque'}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.stockQuantity}
                        onChange={(e) => setFormData(prev => ({ ...prev, stockQuantity: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="100"
                        min="0"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Aviso de Estoque Baixo
                      </label>
                      <input
                        type="number"
                        value={formData.lowStockThreshold}
                        onChange={(e) => setFormData(prev => ({ ...prev, lowStockThreshold: e.target.value }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="5"
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Quantidade mínima para alerta
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código do Lote
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.batchCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, batchCode: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Digite o código do lote (ex: LOTE001, 2024-001)"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Código único para identificar este lote específico no estoque
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Campos específicos para Aluguel */}
          {formData.type === 'rental' && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Configurações de Aluguel</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duração Padrão (dias)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.rentalDuration}
                    onChange={(e) => setFormData(prev => ({ ...prev, rentalDuration: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="7"
                    min="1"
                    max="365"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Quantos dias o cliente terá para usar o item após o resgate
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Como funciona:</strong> Quando o cliente resgatar este prêmio, a data de devolução será automaticamente calculada como {formData.rentalDuration || 7} dias após a data do resgate. Não é necessário definir datas manualmente.
                </p>
              </div>
            </div>
          )}

          {/* Upload de Imagem */}
          <ImageUploader
            currentImage={formData.imageUrl}
            currentImagePath={formData.imagePath}
            onImageChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
            onImagePathChange={(path) => setFormData(prev => ({ ...prev, imagePath: path }))}
            disabled={loading}
            folder="rewards"
          />

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : editingReward ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal para atualizar estoque */}
      <Modal
        isOpen={showStockModal}
        onClose={() => {
          setShowStockModal(false);
          setStockUpdateReward(null);
          setNewStockQuantity('');
        }}
        title="Atualizar Estoque"
      >
        <form onSubmit={submitStockUpdate} className="space-y-4">
          {stockUpdateReward && (
            <>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900">{stockUpdateReward.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Tipo: {getTypeLabel(stockUpdateReward.type)}
                </p>
                <p className="text-sm text-gray-600">
                  Estoque atual: <span className="font-medium">{stockUpdateReward.stockQuantity}</span> {stockUpdateReward.type === 'rental' ? 'disponíveis' : 'unidades'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nova Quantidade em Estoque
                </label>
                <input
                  type="number"
                  min="0"
                  value={newStockQuantity}
                  onChange={(e) => setNewStockQuantity(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite a nova quantidade"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {stockUpdateReward.type === 'rental' 
                    ? 'Quantidade de itens disponíveis para aluguel' 
                    : 'Quantidade em estoque'
                  }
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código do Lote <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={batchCode}
                  onChange={(e) => setBatchCode(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Digite o código do lote (ex: LOTE001, 2024-001)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Código único para identificar este lote específico no estoque
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> Esta ação irá atualizar diretamente o estoque do prêmio. 
                  Certifique-se de que a quantidade está correta.
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowStockModal(false);
                setStockUpdateReward(null);
                setNewStockQuantity('');
                setBatchCode('');
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Atualizando...' : 'Atualizar Estoque'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RewardsManager;
